import { message, Spin, Form, Input } from 'antd';
import Card, { CardEdit } from 'components/Card/Card';
import { useEditCard } from 'components/Card/useEditCard';
import TabularList from 'components/TabularList/TabularList';
import AsyncSelect from 'components/Select/AsyncSelect';
import { useState } from 'react';
import { useAsync } from 'react-async-hook';
import { providerService } from 'services';
import { FACILITY_NAME_REGEX, BUSINESS_NAME_REGEX } from 'constants/index';
import { getStringValidator } from 'utils';
import { TaxDetails } from './CareLocationDetails/TaxDetails';
import { CareLocationAddressAndPhone } from './CareLocationDetails/CareLocationAddressAndPhone';
import { CareLocationContact } from './CareLocationDetails/CareLocationContact';
import { MailingAddress } from './CareLocationDetails/MailingAddress';
import { PaymentDetails } from './CareLocationDetails/PaymentDetails';

export function CareLocationDetails({ application, isEditable = false }) {
  const [careFacility, setCareFacility] = useState(null);
  const [form] = Form.useForm();

  useAsync(async () => {
    if (application?.careFacility?.id !== undefined) {
      try {
        const _careFacility = await providerService.getCenterById(Number(application?.careFacility?.id));
        setCareFacility(_careFacility);
      } catch (error) {
        console.log('Error fetching care facility', error);
        message.error('Unable to get care facility information', 5);
      }
    }
  }, [application]);

  const { isEditing, editProps: basicInfoEditProps } = useEditCard({
    id: 'care-location-basic-info',
    onEditStart: () =>
      form.setFieldsValue({
        type: careFacility?.type?.id,
        doingBusinessAs: careFacility?.doingBusinessAs,
        businessLegalName: careFacility?.businessLegalName,
      }),
    onSave: async () => {
      await form.validateFields();
      const values = form.getFieldsValue();
      const payload = {
        type: typeof values.type === 'number' ? { id: values.type } : values.type,
        doingBusinessAs: values.doingBusinessAs?.trim(),
        businessLegalName: values.businessLegalName?.trim(),
      };
      await providerService.updateCenter(payload, careFacility?.id);
      const updatedCareFacility = await providerService.getCenterById(Number(careFacility?.id));
      setCareFacility(updatedCareFacility);
      form.resetFields();
      message.success('Care location updated');
    },
    onDiscard: () => form.resetFields(),
  });

  return (
    <Spin spinning={careFacility === null}>
      {careFacility && (
        <>
          <Card
            title="Basic information"
            extra={isEditable ? <CardEdit {...basicInfoEditProps} /> : null}
            noBodyPadding
            stickyHeader
          >
            <Form form={form} disabled={basicInfoEditProps.loading}>
              <TabularList
                indented
                data={[
                  {
                    title: 'Care location type',
                    titleRequired: true,
                    value: isEditing ? (
                      <Form.Item
                        name="type"
                        className="mb-0"
                        rules={[{ required: true, message: 'Care location type is required' }]}
                      >
                        <AsyncSelect
                          apiPrefix="/providers"
                          optionsApiUrl="/ref-data/types-of-care"
                          getOptionLabel={(option) => option.title}
                          getOptionValue={(option) => option.id}
                          isOptionSelected={(option, [value]) => option?.id === (value?.id || value)}
                          isClearable={false}
                        />
                      </Form.Item>
                    ) : (
                      careFacility?.type?.title
                    ),
                  },
                  {
                    title: 'Business legal name',
                    titleRequired: true,
                    value: isEditing ? (
                      <Form.Item
                        name="businessLegalName"
                        className="mb-0"
                        rules={[
                          { required: true, message: 'Business legal name is required' },
                          { type: 'string', message: 'Invalid business legal name' },
                          { max: 256, message: 'Business legal name is too long' },
                          { min: 3, message: 'Business legal name is too short' },
                          {
                            pattern: BUSINESS_NAME_REGEX,
                            message: 'Business legal name is invalid',
                          },
                          {
                            validator: getStringValidator('Business legal name is invalid'),
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    ) : (
                      careFacility?.businessLegalName
                    ),
                  },
                  {
                    title: 'Doing business as (DBA)',
                    value: isEditing ? (
                      <Form.Item
                        name="doingBusinessAs"
                        className="mb-0"
                        rules={[
                          { type: 'string', message: 'Invalid Name.' },
                          { max: 256, message: 'Name is too long.' },
                          { min: 3, message: 'Name is too short.' },
                          {
                            pattern: FACILITY_NAME_REGEX,
                            message: 'Name is invalid.',
                          },
                          {
                            validator: getStringValidator('Name is invalid.'),
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    ) : (
                      careFacility?.doingBusinessAs
                    ),
                  },
                ]}
              />
            </Form>
          </Card>

          <TaxDetails
            application={application}
            careFacility={careFacility}
            setCareFacility={setCareFacility}
            isEditable={isEditable}
          />

          <CareLocationAddressAndPhone
            application={application}
            careFacility={careFacility}
            setCareFacility={setCareFacility}
            isEditable={isEditable}
          />

          <CareLocationContact
            application={application}
            careFacility={careFacility}
            setCareFacility={setCareFacility}
            isEditable={isEditable}
          />

          <MailingAddress
            application={application}
            careFacility={careFacility}
            setCareFacility={setCareFacility}
            isEditable={isEditable}
          />

          <PaymentDetails
            application={application}
            careFacility={careFacility}
            setCareFacility={setCareFacility}
            isEditable={isEditable}
          />
        </>
      )}
    </Spin>
  );
}
